import { useEffect, useRef, useState } from 'react';
import {
  chopperOf,
  privateJetsOf,
  scriptOf,
  type AirportContent,
  type FlightView as Flight,
} from '@london-life/contracts';
import { createFlight, type Flight as Scene, type FlightView } from '@london-life/game/flight/scene';
import { drawAdvert, drawBoardPicture } from '@london-life/game/city/props';
import { advertPicture } from '../adverts/use-adverts';

// A flight, as Lagos Life's: the jet itself fills the screen (at the gate, taking off, in the air, landing), with
// the flight's card at the top (number, cabin, the jet's registration, from → to, how far, what is happening on
// board) and three ways to watch: outside, the cabin, or your seat with the moving map. A device that cannot
// draw it keeps the card over the map.
const VIEWS: { id: FlightView; icon: string; name: string }[] = [
  { id: 'outside', icon: '👀', name: 'Outside' },
  { id: 'cabin', icon: '💺', name: 'Cabin' },
  { id: 'seat', icon: '🙋', name: 'My seat' },
];
const VIEW_KEY = 'll.flight.view';
const rememberedView = (): FlightView => {
  try {
    const saved = localStorage.getItem(VIEW_KEY);
    return saved === 'cabin' || saved === 'seat' ? saved : 'outside';
  } catch {
    return 'outside';
  }
};

// what the card says at a moment: where things are, how far through (0–1), the line from the cabin
export function flightStatus(flight: Flight, airport: AirportContent, now: number) {
  const script = scriptOf(airport);
  const boards = Date.parse(flight.boardsAt);
  const off = Date.parse(flight.takesOffAt);
  const lands = Date.parse(flight.landsAt);
  if (now < boards)
    return {
      status: `Cab to ${flight.from.name} · ${Math.ceil((boards - now) / 1000)}s`,
      progress: 0,
      line: `🚕 On the way to ${flight.from.name} for ${flight.number}`,
    };
  if (now < off)
    return {
      status: `Boarding · gate ${flight.gate} · ${Math.ceil((off - now) / 1000)}s`,
      progress: 0,
      line: script.boarding,
    };
  const through = Math.min(1, (now - off) / Math.max(1, lands - off));
  if (flight.mode === 'chopper' && now < lands) {
    const lines = chopperOf(airport).lines;
    return {
      status: `${Math.max(1, Math.round(((lands - now) / 1000) * 2))} min to landing`,
      progress: through,
      line: through < 0.25 ? lines.lift : through < 0.75 ? lines.over : lines.down.replace('{place}', flight.to.name),
    };
  }
  if (now >= lands) return { status: 'Landed', progress: 1, line: script.landed.replace('{airport}', flight.to.name) };
  const said = [...script.lines].sort((a, b) => a.at - b.at).filter((item) => item.at <= through);
  return {
    // a game minute is two real seconds
    status: `${Math.max(1, Math.round(((lands - now) / 1000) * 2))} min to landing`,
    progress: through,
    line: said.length ? said[said.length - 1]!.text : script.takeOff,
  };
}

export function FlightScreen({
  flight,
  airport,
  night,
  dataSaver,
  boardAd,
  owner,
  onBringAlong,
}: {
  flight: Flight;
  airport: AirportContent;
  night: boolean;
  dataSaver: boolean;
  // a player's ad on an airport board (its picture's path), shown on the board by the gate
  boardAd: string | null;
  // what is painted on the player's own jet: their name
  owner?: string;
  // "Bring someone along" (before take-off, on my own flight)
  onBringAlong?: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const scene = useRef<Scene | null>(null);
  const [view, setView] = useState<FlightView>(rememberedView);
  const [drawn, setDrawn] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, []);
  const latest = useRef({ view });
  latest.current = { view };
  const jets = privateJetsOf(airport);
  useEffect(() => {
    const element = box.current;
    if (!element) return;
    const own = flight.mode === 'jet';
    const made = createFlight(element, {
      mode: flight.mode === 'train' ? 'air' : flight.mode,
      // the airline's paint, or the player's own jet with their name on it
      livery: own
        ? {
            ...jets.colours,
            name: owner ?? flight.jetName,
            code:
              (owner ?? flight.jetName)
                .replace(/[^A-Za-z]/g, '')
                .slice(0, 2)
                .toUpperCase() || 'LL',
            registration: flight.registration,
          }
        : {
            ...airport.airline.colours,
            name: airport.airline.name,
            code: airport.airline.code,
            registration: flight.registration,
          },
      takesOffAt: Date.parse(flight.takesOffAt),
      landsAt: Date.parse(flight.landsAt),
      from: flight.from,
      to: flight.to,
      night,
      dataSaver,
      view: flight.mode === 'chopper' ? 'outside' : latest.current.view,
      advert: drawAdvert(`Fly ${airport.airline.name}`, airport.airline.colours.tail, '#ffffff', 512, 256),
    });
    if (!made.ok) {
      setDrawn(false);
      return;
    }
    scene.current = made;
    return () => {
      made.dispose();
      scene.current = null;
    };
    // one scene per flight
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flight.id]);
  // a player's airport ad on the board by the gate
  useEffect(() => {
    if (!boardAd) return;
    let live = true;
    void advertPicture(boardAd).then((image) => {
      if (live && image) scene.current?.setAdvert(drawBoardPicture(image, 28, 14));
      image?.close();
    });
    return () => {
      live = false;
    };
  }, [boardAd, flight.id]);
  // the jet waits at the gate for people hopping in (or leaves sooner once they have)
  useEffect(() => {
    scene.current?.setTimes(Date.parse(flight.takesOffAt), Date.parse(flight.landsAt));
  }, [flight.takesOffAt, flight.landsAt]);
  useEffect(() => {
    scene.current?.setView(view);
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      // remembered only where the browser allows it
    }
  }, [view]);
  const { status, progress, line } = flightStatus(flight, airport, now);
  const cabin =
    flight.mode === 'jet'
      ? '🛩️ Your jet'
      : flight.mode === 'chopper'
        ? '🚁 VIP chopper'
        : airport.cabins[flight.cabin].label;
  const atGate = now < Date.parse(flight.takesOffAt);
  const company = flight.host
    ? `🫶 On ${flight.host}'s flight${flight.crew.length ? ` with ${flight.crew.join(', ')}` : ''}`
    : flight.crew.length
      ? `🫶 With ${flight.crew.join(', ')}${flight.waiting ? ` · ⏳ ${flight.waiting} deciding` : ''}`
      : flight.waiting
        ? '⏳ Seats offered · waiting for them to hop in'
        : null;
  return (
    <>
      {drawn && <div className="ride-screen flight-screen" ref={box} />}
      <section className="flight-card" aria-label={`Flight ${flight.number}`} role="status">
        <div className="flight-card-top">
          <small>
            {flight.mode === 'air'
              ? `${flight.number} · ${cabin}`
              : flight.mode === 'jet'
                ? `${cabin} · ${flight.jetName}`
                : cabin}
          </small>
          <small>{flight.registration}</small>
        </div>
        <div className="flight-card-route">
          <strong>{flight.from.code}</strong>
          <span aria-hidden="true">→</span>
          <strong>{flight.to.code}</strong>
        </div>
        <div
          className="flight-card-bar"
          role="progressbar"
          aria-label="How far"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <i style={{ width: `${progress * 100}%` }} />
        </div>
        <small className="flight-card-status">{status}</small>
        <p className="flight-card-line">{line}</p>
      </section>
      {company ? (
        <div className="flight-along" role="status">
          {company}
        </div>
      ) : (
        atGate &&
        onBringAlong && (
          <button type="button" className="flight-along" onClick={onBringAlong}>
            👥 Bring someone along
          </button>
        )
      )}
      {drawn && (
        <div className="flight-views" role="group" aria-label="How to watch">
          {VIEWS.filter((item) => flight.mode !== 'chopper' || item.id === 'outside').map((item) => (
            <button type="button" key={item.id} aria-pressed={view === item.id} onClick={() => setView(item.id)}>
              <span aria-hidden="true">{item.icon}</span> {item.name}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
